import { useEffect, useState, type ReactNode } from "react";
import type { DashboardLoadState } from "../lib/dashboard";
import { EmptyState } from "../components/EmptyState";

export function useDashboardQuery<T>(
  loader: () => Promise<DashboardLoadState<T>>
): DashboardLoadState<T> {
  const [state, setState] = useState<DashboardLoadState<T>>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    void (async () => {
      const next = await loader();
      if (!cancelled) setState(next);
    })();
    return () => {
      cancelled = true;
    };
    // Stable module-level loaders only — do not pass inline closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

export function DashboardStateView<T>({
  state,
  emptyTitle,
  emptyDetail,
  children,
}: {
  state: DashboardLoadState<T>;
  emptyTitle: string;
  emptyDetail: string;
  children: (data: T) => ReactNode;
}) {
  if (state.status === "loading") {
    return (
      <div className="panel">
        <div className="panel-body">
          <p style={{ color: "var(--text-muted)" }}>Loading tenant data…</p>
        </div>
      </div>
    );
  }
  if (state.status === "denied") {
    return (
      <EmptyState
        title="Access denied"
        detail={state.message || "You do not have permission for this section."}
      />
    );
  }
  if (state.status === "error") {
    return <EmptyState title="Unable to load" detail={state.message} />;
  }
  if (state.status === "empty") {
    return (
      <>
        <EmptyState title={emptyTitle} detail={emptyDetail} />
        {children(state.data)}
      </>
    );
  }
  return <>{children(state.data)}</>;
}
